import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../api/api';
import { TrendingUp, DollarSign, Calendar, ChevronDown, ChevronRight, RefreshCw, AlertTriangle, Truck, BarChart3, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n || 0);
const formatPct = (n) => `${(n || 0).toFixed(1)}%`;
const formatNum = (n) => new Intl.NumberFormat('es-ES', { maximumFractionDigits: 2 }).format(n || 0);
const formatEntero = (n) => new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n || 0);

const CONCEPTOS_GASTO = [
  ['ingredientes', 'Ingredientes'],
  ['consumibles', 'Consumibles'],
  ['bebidas', 'Bebidas'],
  ['alquiler', 'Alquiler'],
  ['personal_extra', 'Personal extra'],
];

// "Ingredientes 1.200 · Alquiler 715 · Personal extra 480" (solo los que no son cero)
const textoConceptos = (porConcepto) => CONCEPTOS_GASTO
  .filter(([clave]) => Math.abs(Number(porConcepto?.[clave]) || 0) >= 0.005)
  .map(([clave, etiqueta]) => `${etiqueta} ${formatEntero(porConcepto[clave])}`)
  .join(' · ');

export default function ProveedorEstadisticas() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearsDisp, setYearsDisp] = useState([]);
  const [expandedProv, setExpandedProv] = useState(null);
  const [sortBy, setSortBy] = useState('gasto_total');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMonths, setSelectedMonths] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      let url = `${API_URL}/api/proveedores/estadisticas?year=${selectedYear}`;
      if (selectedMonths.length > 0) {
        url += `&months=${selectedMonths.join(',')}`;
      }
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await res.json();
      setData(d);
      if (d.years_disponibles?.length) setYearsDisp(d.years_disponibles);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedYear, selectedMonths]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleMonth = (mes) => {
    setSelectedMonths(prev => {
      if (prev.includes(mes)) {
        return prev.filter(m => m !== mes);
      } else {
        return [...prev, mes].sort((a, b) => a - b);
      }
    });
  };

  const clearMonths = () => {
    setSelectedMonths([]);
  };

  const sortedProveedores = data?.proveedores ? [...data.proveedores].sort((a, b) => {
    const va = a[sortBy] || 0, vb = b[sortBy] || 0;
    return sortDir === 'desc' ? vb - va : va - vb;
  }) : [];

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(field); setSortDir('desc'); }
  };

  const toggleExpand = (provId) => {
    setExpandedProv(prev => prev === provId ? null : provId);
  };

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      className={`px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer hover:text-slate-800 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {children}
        {sortBy === field && <span className="text-[10px]">{sortDir === 'desc' ? '\u25BC' : '\u25B2'}</span>}
      </span>
    </th>
  );

  const maxMensualGasto = data ? Math.max(...data.tendencia_mensual.map(m => m.gasto), 1) : 1;

  const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  if (loading && !data) return (
    <div className="flex items-center justify-center py-20">
      <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      <span className="ml-3 text-slate-500">Cargando estadisticas...</span>
    </div>
  );

  if (!data) return <div className="text-center py-20 text-slate-400">No se pudieron cargar las estadisticas</div>;

  const r = data.resumen;

  return (
    <div className="space-y-6" data-testid="proveedor-estadisticas">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Estadisticas de Gasto por Proveedor</h2>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
          <Calendar className="w-4 h-4 text-slate-400" />
          <select
            data-testid="prov-stats-year-selector"
            value={selectedYear}
            onChange={(e) => { setSelectedYear(parseInt(e.target.value)); setSelectedMonths([]); }}
            className="text-sm font-medium text-slate-700 bg-transparent border-none outline-none cursor-pointer"
          >
            {yearsDisp.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="prov-stats-kpis">
        <KpiCard icon={<DollarSign className="w-4 h-4" />} label="Gasto Total" value={formatCurrency(r.gasto_total)} color="rose" />
        <KpiCard icon={<Truck className="w-4 h-4" />} label="Proveedores" value={r.num_proveedores_activos} color="blue" />
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label="Top Proveedor" value={r.top_proveedor} subvalue={formatCurrency(r.top_proveedor_gasto)} color="amber" />
        <KpiCard icon={<AlertTriangle className="w-4 h-4" />} label="Concentracion Top 3" value={formatPct(r.concentracion_top3)} color={r.concentracion_top3 > 70 ? 'rose' : r.concentracion_top3 > 50 ? 'amber' : 'emerald'} />
        <KpiCard icon={<BarChart3 className="w-4 h-4" />} label="Eventos" value={r.num_presupuestos} color="slate" />
      </div>

      {/* Monthly trend chart - clickable bars */}
      <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="prov-tendencia-mensual">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700">
            Gasto Mensual {selectedYear}
            {selectedMonths.length > 0 && (
              <span className="ml-2 text-xs font-normal text-slate-400">
                (filtrado: {selectedMonths.map(m => MONTH_NAMES[m - 1]).join(', ')})
              </span>
            )}
          </h3>
          <div className="flex items-center gap-2">
            {loading && <RefreshCw className="w-3.5 h-3.5 animate-spin text-slate-400" />}
            {selectedMonths.length > 0 && (
              <button
                onClick={clearMonths}
                data-testid="clear-month-filter"
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-500 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
              >
                <X className="w-3 h-3" />
                Limpiar filtro
              </button>
            )}
          </div>
        </div>
        <p className="text-[10px] text-slate-400 mb-2">Haz clic en las barras para filtrar por mes</p>
        <div className="flex items-end gap-1.5" style={{ minHeight: '200px' }}>
          {data.tendencia_mensual.map(m => {
            const pct = maxMensualGasto > 0 ? (m.gasto / maxMensualGasto) * 100 : 0;
            const currentMonth = new Date().getMonth() + 1;
            const isCurrent = m.mes === currentMonth && selectedYear === new Date().getFullYear();
            const isSelected = selectedMonths.includes(m.mes);
            const hasFilter = selectedMonths.length > 0;
            const topIng = m.top_ingredientes?.[0];

            // Bar color: selected=rose, current=rose (lighter if not selected), has data=slate, empty=slate-200
            let barColor;
            if (isSelected) {
              barColor = 'bg-rose-500';
            } else if (hasFilter) {
              barColor = m.gasto > 0 ? 'bg-slate-300' : 'bg-slate-100';
            } else if (isCurrent) {
              barColor = 'bg-rose-500';
            } else {
              barColor = m.gasto > 0 ? 'bg-slate-700' : 'bg-slate-200';
            }

            return (
              <div
                key={m.mes}
                className="flex-1 flex flex-col items-center gap-1 group relative cursor-pointer"
                onClick={() => m.gasto > 0 && toggleMonth(m.mes)}
                data-testid={`month-bar-${m.mes}`}
              >
                {/* Tooltip con ranking top 5 ingredientes */}
                {m.top_ingredientes?.length > 0 && (
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-56 bg-slate-800 text-white rounded-lg p-2.5 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                    <p className="text-[10px] font-bold text-slate-300 uppercase tracking-wide mb-1.5">Top {m.nombre_mes} {selectedYear}</p>
                    {m.top_ingredientes.map((ti, i) => (
                      <div key={i} className="flex items-center gap-1.5 py-0.5">
                        <span className={`text-[10px] font-bold w-4 text-center ${i === 0 ? 'text-amber-400' : 'text-slate-400'}`}>{i + 1}</span>
                        <span className="text-[10px] text-slate-200 truncate flex-1">{ti.nombre}</span>
                        <span className="text-[10px] font-semibold text-slate-300">{formatCurrency(ti.gasto)}</span>
                      </div>
                    ))}
                    <div className="text-[9px] text-slate-400 mt-1.5 pt-1.5 border-t border-slate-700">
                      Gasto total: {formatCurrency(m.gasto)}
                    </div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-slate-800" />
                  </div>
                )}
                <div className="w-full flex flex-col items-center justify-end h-28">
                  <div
                    className={`w-full max-w-[40px] rounded-t-md transition-all ${barColor} ${isSelected ? 'ring-2 ring-rose-300 ring-offset-1' : ''}`}
                    style={{ height: `${Math.max(pct > 0 ? 6 : 2, pct)}%` }}
                  />
                </div>
                <span className={`text-[10px] ${isSelected ? 'font-bold text-rose-600' : isCurrent ? 'font-bold text-rose-600' : 'text-slate-400'}`}>{m.nombre_mes}</span>
                {topIng ? (
                  <span className="text-[9px] text-slate-500 font-medium truncate w-full text-center px-0.5" title={topIng.nombre}>
                    {topIng.nombre.length > 12 ? topIng.nombre.substring(0, 12) + '...' : topIng.nombre}
                  </span>
                ) : m.gasto > 0 ? (
                  <span className="text-[9px] text-slate-400">{formatCurrency(m.gasto)}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      {/* Proveedores ranking table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ranking-proveedores">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">
            Ranking de Proveedores
            {selectedMonths.length > 0 && (
              <span className="ml-2 text-xs font-normal text-rose-500">
                {selectedMonths.map(m => MONTH_NAMES[m - 1]).join(' + ')}
              </span>
            )}
          </h3>
          {selectedMonths.length > 0 && (
            <span className="text-xs text-slate-400">
              {sortedProveedores.length} proveedores
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-8">#</th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide w-8"></th>
                <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Proveedor</th>
                <SortHeader field="gasto_total" className="text-right">Gasto</SortHeader>
                <SortHeader field="porcentaje_gasto" className="text-center">% Total</SortHeader>
                <SortHeader field="num_eventos" className="text-center">Eventos</SortHeader>
                <SortHeader field="num_ingredientes" className="text-center">Ingredientes</SortHeader>
                <th className="px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide text-center">Estacionalidad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {sortedProveedores.map((prov, i) => {
                const rank = i + 1;
                const isExpanded = expandedProv === prov.proveedor_id;
                const mensualValues = Object.values(prov.mensual || {}).map(Number);
                const maxM = Math.max(...mensualValues, 1);

                return (
                  <React.Fragment key={prov.proveedor_id}>
                    <tr
                      className="hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => toggleExpand(prov.proveedor_id)}
                      data-testid={`prov-row-${rank}`}
                    >
                      <td className="px-3 py-2.5 text-slate-400 font-mono text-xs">{rank}</td>
                      <td className="px-1 py-2.5">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="font-medium text-slate-800">{prov.nombre}</span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <div className="font-semibold text-rose-700">{formatCurrency(prov.gasto_total)}</div>
                        {textoConceptos(prov.por_concepto) && (
                          <div className="text-[10px] text-slate-400 whitespace-nowrap" data-testid={`prov-conceptos-${rank}`}>
                            {textoConceptos(prov.por_concepto)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <div className="w-16 bg-slate-100 rounded-full h-1.5">
                            <div className="bg-rose-500 h-1.5 rounded-full" style={{ width: `${Math.min(prov.porcentaje_gasto, 100)}%` }} />
                          </div>
                          <span className="text-xs text-slate-600 w-10 text-right">{formatPct(prov.porcentaje_gasto)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center text-slate-600">{prov.num_eventos}</td>
                      <td className="px-3 py-2.5 text-center text-slate-600">{prov.num_ingredientes}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-end gap-px h-5 justify-center">
                          {mensualValues.map((v, mi) => {
                            const isMonthSelected = selectedMonths.includes(mi + 1);
                            return (
                              <div
                                key={mi}
                                className={`w-1.5 rounded-t-sm ${isMonthSelected ? 'bg-rose-500' : v > 0 ? 'bg-slate-400' : 'bg-slate-100'}`}
                                style={{ height: `${Math.max(v > 0 ? 15 : 3, (v / maxM) * 100)}%` }}
                              />
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                    {/* Expanded ingredient detail */}
                    {isExpanded && (
                      <tr data-testid={`prov-detail-${rank}`}>
                        <td colSpan={8} className="px-0 py-0">
                          <div className="bg-slate-50 border-t border-slate-200">
                            <div className="px-6 py-3">
                              {prov.eventos && prov.eventos.length > 0 && (
                                <div className="mb-4">
                                  <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                    Eventos con gasto en {prov.nombre}
                                    {selectedMonths.length > 0 && (
                                      <span className="ml-1 text-rose-500 font-normal">
                                        ({selectedMonths.map(m => MONTH_NAMES[m - 1]).join(' + ')})
                                      </span>
                                    )}
                                    <span className="ml-1 text-slate-400 font-normal">· {prov.eventos.length}</span>
                                  </h4>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-slate-500">
                                        <th className="text-left py-1.5 pr-3 font-semibold">Fecha</th>
                                        <th className="text-left py-1.5 px-2 font-semibold">Nº</th>
                                        <th className="text-left py-1.5 px-2 font-semibold">Cliente</th>
                                        <th className="text-right py-1.5 pl-2 font-semibold">Gasto</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                      {prov.eventos.map((ev, ei) => (
                                        <tr key={ei} className="hover:bg-white/60">
                                          <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">
                                            {ev.fecha_evento ? new Date(ev.fecha_evento).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' }) : '—'}
                                          </td>
                                          <td className="py-1.5 px-2 text-slate-600 whitespace-nowrap">
                                            {ev.presupuesto_id ? (
                                              <a href={`/presupuestos/${ev.presupuesto_id}`} target="_blank" rel="noopener noreferrer" className="text-rose-600 hover:underline">
                                                {ev.numero || 'ver'}
                                              </a>
                                            ) : (ev.numero || '—')}
                                          </td>
                                          <td className="py-1.5 px-2 text-slate-700 font-medium">{ev.cliente_nombre || '—'}</td>
                                          <td className="py-1.5 pl-2 text-right font-bold text-rose-700">{formatCurrency(ev.gasto)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">
                                Desglose de Ingredientes - {prov.nombre}
                                {selectedMonths.length > 0 && (
                                  <span className="ml-1 text-rose-500 font-normal">
                                    ({selectedMonths.map(m => MONTH_NAMES[m - 1]).join(' + ')})
                                  </span>
                                )}
                              </h4>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-slate-500">
                                    <th className="text-left py-1.5 pr-3 font-semibold">Ingrediente</th>
                                    <th className="text-right py-1.5 px-2 font-semibold">Precio Ud.</th>
                                    <th className="text-right py-1.5 px-2 font-semibold">Cantidad</th>
                                    <th className="text-left py-1.5 px-1 font-semibold">Ud.</th>
                                    <th className="text-right py-1.5 px-2 font-semibold">Gasto Ud.</th>
                                    <th className="text-right py-1.5 px-2 font-semibold">Uds/Envase</th>
                                    <th className="text-right py-1.5 px-2 font-semibold">Envases</th>
                                    <th className="text-right py-1.5 px-2 font-semibold">Uds Totales</th>
                                    <th className="text-right py-1.5 px-2 font-semibold">Coste Envases</th>
                                    <th className="text-right py-1.5 pl-2 font-semibold">% Prov.</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                  {prov.ingredientes.map((ing, ii) => {
                                    const pctIng = prov.gasto_total > 0 ? (ing.coste_envases / prov.gasto_total * 100) : 0;
                                    return (
                                      <tr key={ii} className="hover:bg-white/60">
                                        <td className="py-1.5 pr-3 text-slate-700 font-medium">{ing.nombre}</td>
                                        <td className="py-1.5 px-2 text-right text-slate-400">{formatCurrency(ing.precio_unitario)}</td>
                                        <td className="py-1.5 px-2 text-right text-slate-500">{formatNum(ing.cantidad_total)}</td>
                                        <td className="py-1.5 px-1 text-slate-400">{ing.unidad}</td>
                                        <td className="py-1.5 px-2 text-right text-slate-400">{formatCurrency(ing.gasto_total)}</td>
                                        <td className="py-1.5 px-2 text-right text-slate-500">{formatNum(ing.unidades_por_paquete)}</td>
                                        <td className="py-1.5 px-2 text-right font-medium text-slate-700">{ing.envases_necesarios}</td>
                                        <td className="py-1.5 px-2 text-right text-slate-600">{formatNum(ing.cantidad_envases)} {ing.unidad}</td>
                                        <td className="py-1.5 px-2 text-right font-bold text-rose-700">{formatCurrency(ing.coste_envases)}</td>
                                        <td className="py-1.5 pl-2 text-right">
                                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${pctIng >= 30 ? 'bg-rose-100 text-rose-700' : pctIng >= 15 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                            {formatPct(pctIng)}
                                          </span>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            {sortedProveedores.length > 0 && (
              <tfoot className="bg-slate-800 text-white">
                <tr>
                  <td colSpan={3} className="px-3 py-2.5 font-bold text-sm">TOTAL</td>
                  <td className="px-3 py-2.5 text-right font-bold">{formatCurrency(r.gasto_total)}</td>
                  <td className="px-3 py-2.5 text-center font-bold">100%</td>
                  <td className="px-3 py-2.5 text-center">{r.num_presupuestos}</td>
                  <td colSpan={2} className="px-3 py-2.5"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {sortedProveedores.length === 0 && (
          <div className="p-8 text-center text-slate-400">No hay datos de gasto para {selectedYear}{selectedMonths.length > 0 ? ` (${selectedMonths.map(m => MONTH_NAMES[m - 1]).join(', ')})` : ''}</div>
        )}
      </div>

      {/* Info note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-500">
        <strong>Nota:</strong> Haz clic en las barras del grafico para filtrar por mes (multi-seleccion).
        <strong> Coste Envases</strong> = envases necesarios x precio por envase.
        <strong> Cant. Envases</strong> = envases x uds/envase (cantidad real comprada).
        <strong> Gasto Ud.</strong> = cantidad teórica x precio unitario.
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, subvalue, color }) {
  const colors = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${colors[color] || colors.slate}`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-1.5 mb-1 opacity-75">{icon}<span className="text-[10px] font-semibold uppercase tracking-wide">{label}</span></div>
      <p className="text-lg font-bold truncate" title={typeof value === 'string' ? value : ''}>{value}</p>
      {subvalue && <p className="text-xs opacity-75 mt-0.5">{subvalue}</p>}
    </div>
  );
}
